import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Snackbar,
  IconButton,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/api';

interface Admin {
  id: number;
  name: string;
}

interface CreateAdminRequest {
  name: string;
  password: string;
}

interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const AdminManagement: React.FC = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentAdmin, setCurrentAdmin] = useState<Admin | null>(null);
  
  // 다이얼로그 상태
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  
  // 폼 상태
  const [createForm, setCreateForm] = useState<CreateAdminRequest>({
    name: '',
    password: '',
  });
  const [passwordForm, setPasswordForm] = useState<ChangePasswordRequest>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // 현재 로그인된 관리자 정보 로드
  const loadCurrentAdmin = async () => {
    try {
      const response = await adminAPI.getCurrentAdmin();
      console.log('Current Admin Response:', response);
      const adminData = response.data.data;
      setCurrentAdmin({
        id: adminData.id || adminData.ID, // 소문자 우선, 대문자 fallback
        name: adminData.name || adminData.Name, // 소문자 우선, 대문자 fallback
      });
    } catch (err: any) {
      console.error('Current Admin Error:', err);
      // 로그인되지 않은 경우 로그인 페이지로 리다이렉트
      window.location.href = '/login';
    }
  };

  // 관리자 목록 로드
  const loadAdmins = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAdmins();
      console.log('API Response:', response); // 디버깅용
      
      // API 응답 구조: { success: true, data: [...] }
      const rawAdmins = response.data.data || [];
      
      // 필드명 매핑 (백엔드 응답 구조에 맞춤)
      const mappedAdmins = rawAdmins.map((admin: any) => ({
        id: admin.id || admin.ID, // 소문자 우선, 대문자 fallback
        name: admin.name || admin.Name, // 소문자 우선, 대문자 fallback
      }));
      
      setAdmins(mappedAdmins);
      setError(null);
    } catch (err: any) {
      console.error('API Error:', err); // 디버깅용
      setError(err.response?.data?.error || '관리자 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 관리자 생성
  const handleCreateAdmin = async () => {
    try {
      await adminAPI.createAdmin(createForm);
      setSuccess('관리자가 성공적으로 생성되었습니다.');
      setCreateDialogOpen(false);
      setCreateForm({ name: '', password: '' });
      loadAdmins();
    } catch (err: any) {
      setError(err.response?.data?.error || '관리자 생성에 실패했습니다.');
    }
  };

  // 비밀번호 변경
  const handleChangePassword = async () => {
    // 유효성 검사
    if (!passwordForm.currentPassword.trim()) {
      setError('현재 비밀번호를 입력해주세요.');
      return;
    }
    
    if (!passwordForm.newPassword.trim()) {
      setError('새 비밀번호를 입력해주세요.');
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      setError('새 비밀번호는 최소 8자 이상이어야 합니다.');
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    try {
      await adminAPI.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword,
      });
      setSuccess('비밀번호가 성공적으로 변경되었습니다. 다시 로그인해주세요.');
      setPasswordDialogOpen(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      // 로그아웃 처리
      setTimeout(() => {
        window.location.href = 'http://localhost:3000/login';
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || '비밀번호 변경에 실패했습니다.');
    }
  };

  // 로그아웃
  const handleLogout = async () => {
    try {
      await adminAPI.logout();
      window.location.href = 'http://localhost:3000/login';
    } catch (err) {
      setError('로그아웃에 실패했습니다.');
    }
  };

  useEffect(() => {
    loadCurrentAdmin(); // 먼저 현재 관리자 정보 로드
    loadAdmins(); // 그 다음 관리자 목록 로드
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>로딩 중...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* 헤더 */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Admin Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          관리자 추가
        </Button>
      </Box>

      {/* 현재 로그인된 관리자 정보 */}
      {currentAdmin && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ backgroundColor: 'primary.light', mr: 2 }}>
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  현재 로그인된 관리자: {currentAdmin.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  관리자 ID: {currentAdmin.id}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                onClick={handleLogout}
                sx={{ ml: 'auto' }}
              >
                로그아웃
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* 통계 카드 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ backgroundColor: 'primary.light', mr: 2 }}>
                  <AdminIcon />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    총 관리자 수
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {admins.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 관리자 목록 */}
      <Card elevation={2}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
            관리자 목록
          </Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>이름</TableCell>
                  <TableCell>상태</TableCell>
                  <TableCell align="right">작업</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.id} hover>
                    <TableCell>{admin.id}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: 'primary.main' }}>
                          <PersonIcon />
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {admin.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label="활성" color="success" size="small" />
                    </TableCell>
                    <TableCell align="right">
                      {currentAdmin && admin.id === currentAdmin.id && (
                        <IconButton
                          size="small"
                          onClick={() => setPasswordDialogOpen(true)}
                          title="비밀번호 변경"
                        >
                          <LockIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 관리자 생성 다이얼로그 */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>새 관리자 추가</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="관리자 이름"
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="비밀번호"
              type="password"
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              margin="normal"
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>취소</Button>
          <Button onClick={handleCreateAdmin} variant="contained">
            생성
          </Button>
        </DialogActions>
      </Dialog>

      {/* 비밀번호 변경 다이얼로그 */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>비밀번호 변경</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="현재 비밀번호"
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="새 비밀번호"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="새 비밀번호 확인"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              margin="normal"
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>취소</Button>
          <Button onClick={handleChangePassword} variant="contained">
            변경
          </Button>
        </DialogActions>
      </Dialog>

      {/* 알림 */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminManagement;
